import React, { FC, useState } from 'react';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';
import t from '@i18n/t';
import { Column, Row } from '../../common/layoutUtils';
import RadioButton from '../../common/radioButton/RadioButton';
import { ReactComponent as CustomThemeIcon } from '@assets/icons/theme.svg';
import MaterialIcon from '../../common/icons/MaterialIcon';
import colors from '@utils/colors';
import { appThemes, currentTheme as appTheme, customAppTheme, encodeColorScheme } from '@utils/theme/appTheme';
import { settingsActions } from '@state/settings';
import { AppTheme } from '@utils/theme/types';
import ColorPickerTippy from '../../common/colorPicker/ColorPickerTippy';
import { hideAllTippies } from '@state/common/utils';

const Preference: FC = () => {
  const dispatch = useDispatch();
  const currentTheme = appTheme;
  const [theme, setTheme] = useState(currentTheme);
  // A custom colour is the one without a preset key.
  const [customColor, setCustomColor] = useState(currentTheme.key ? '' : currentTheme.primary);

  // Saved to the account, as the rest of the settings are (Petr, 2026-10-08); the page reloads in it once saved.
  const save = (picked: AppTheme) => {
    setTheme(picked);
    dispatch(settingsActions.saveUserSettings.request({ colorScheme: encodeColorScheme(picked) }));
  };

  // A custom colour shows on its swatch as it is picked, and is saved only when the picker's Save is pressed.
  const pickCustom = (value: string, saving: boolean) => {
    setCustomColor(value);
    const picked = customAppTheme(value);
    if (saving) save(picked);
    else setTheme(picked);
  };

  return (
    <ThemesWrapper>
      {appThemes.map((item, index) => {
        return (
          <Theme key={index}>
            <ThemeIcon className={'theme-icon'} color={item.primary} />
            <RadioButton
              checked={item.key === theme.key}
              onChange={() => save(item)}
              className={'radio-button'}
              color={colors.lightgray}
            />
            <SwatchName>{item.name}</SwatchName>
          </Theme>
        );
      })}
      {/* Plainly an option of its own before a colour is picked (Petr, 2026-10-11: make it obvious a custom colour is
          possible): a dashed outline, a colour wheel on its badge and "Pick your own" under it. The whole tile opens
          the picker. */}
      <Theme>
        <ThemeIcon color={customColor} className={'theme-icon'} $empty={!customColor} />
        <ColorPickerTippy
          target={color => (
            <CustomColorWrapper color={customColor}>
              <MaterialIcon name={'format_color_fill'} color={customColor ? colors.darkGray : colors.white} />
            </CustomColorWrapper>
          )}
          onSave={value => {
            pickCustom(value, true);
            hideAllTippies();
          }}
          onChange={value => {
            pickCustom(value!, false);
          }}
          selectedColor={customColor}
          themeColorPicker
        />
        <SwatchName>{customColor ? t.CUSTOM : t.PICK_YOUR_OWN_COLOR}</SwatchName>
      </Theme>
    </ThemesWrapper>
  );
};

export default Preference;

const ThemesWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
`;

const Theme = styled(Column)`
  margin: 0px 10px 0px 0px;
  box-sizing: border-box;
  flex: 1;
  max-width: 80px;
  position: relative;
  > svg {
    margin-bottom: 10px;
    width: 100%;
    height: 100%;
  }
  .radio-button {
    position: absolute;
    top: 0px;
    left: 56px;
    > i {
      font-size: 24px;
    }
  }
`;

const ThemeIcon = styled(CustomThemeIcon)<{ color?: string; $empty?: boolean }>`
  ${props => props.$empty && `*[class='border'] { stroke-dasharray: 4 3; }`}
  circle {
    fill: ${props => props.theme.themeIconCircle};
  }
  *[class='body'] {
    fill: ${props => `${props.color}` || '#C8D1D9'} !important;
  }
  > *[class='check-box'],
  *[class='border'] {
    stroke: ${props => props.color || '#C8D1D9'} !important;
  }
  > *[class='row'],
  *[class='header'] {
    fill: ${props => props.color || '#C8D1D9'} !important;
  }
`;

// The picked colour, or a colour wheel until there is one.
const CustomColorWrapper = styled(Row)<{ color?: string }>`
  background: ${props =>
    props.color || 'conic-gradient(#FF6473, #FFB547, #F5E04A, #1AB77F, #56CCF2, #786FFF, #E35DB4, #FF6473)'};
  position: absolute;
  border-radius: 50%;
  top: 2px;
  left: 58px;
  height: 20px;
  width: 20px;
  align-items: center;
  justify-content: center;
  i {
    font-size: 12px;
  }
`;

const SwatchName = styled.span`
  margin-top: -2px;
  font-size: 13px;
  font-weight: 500;
  text-align: center;
  white-space: nowrap;
  color: ${({ theme }) => theme.textColor};
`;
